import { Link, NavLink, useNavigate } from "react-router-dom";
import { Compass, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/lib/auth";

const navLinks = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/browse", label: "Browse Schools", testid: "nav-browse" },
  { to: "/submit", label: "Submit School", testid: "nav-submit" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-dvh bg-[#FDFBF7] flex flex-col">
      <header className="sticky top-0 z-40 bg-[#FDFBF7]/85 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center gap-2 group"
          >
            <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#1E3A2B] text-[#DAA520] group-hover:bg-[#C85A32] transition-colors">
              <Compass className="w-5 h-5" />
            </span>
            <div className="leading-tight">
              <div className="font-display font-bold text-stone-900 text-base">
                Best Schools
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C85A32] font-semibold">
                in Abuja
              </div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-7">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive
                      ? "text-[#C85A32]"
                      : "text-stone-700 hover:text-stone-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {admin ? (
              <div className="flex items-center gap-3">
                <Link
                  to="/admin"
                  data-testid="nav-admin-dashboard"
                  className="text-sm font-medium text-[#1E3A2B] hover:text-[#C85A32]"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  data-testid="nav-admin-logout"
                  className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/admin/login"
                data-testid="nav-admin-login"
                className="text-sm text-stone-500 hover:text-stone-900"
              >
                Admin
              </Link>
            )}
          </nav>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="md:hidden w-10 h-10 rounded-lg hover:bg-stone-100 flex items-center justify-center text-stone-700"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-stone-200 bg-[#FDFBF7]">
            <nav className="px-4 py-3 flex flex-col gap-1">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2.5 rounded-lg text-sm font-medium ${
                      isActive
                        ? "bg-[#C85A32]/10 text-[#C85A32]"
                        : "text-stone-700 hover:bg-stone-100"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              {admin ? (
                <>
                  <Link
                    to="/admin"
                    onClick={() => setOpen(false)}
                    className="px-3 py-2.5 rounded-lg text-sm font-medium text-[#1E3A2B] hover:bg-stone-100"
                  >
                    Admin Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      setOpen(false);
                      handleLogout();
                    }}
                    className="px-3 py-2.5 rounded-lg text-sm text-stone-600 text-left hover:bg-stone-100"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/admin/login"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2.5 rounded-lg text-sm text-stone-500 hover:bg-stone-100"
                >
                  Admin Login
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-stone-200 bg-[#1E3A2B] text-[#FDFBF7] mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#C85A32] text-white">
                <Compass className="w-5 h-5" />
              </span>
              <div className="font-display font-bold text-xl">
                Best Schools in Abuja
              </div>
            </div>
            <p className="text-sm text-stone-300 leading-relaxed max-w-xs">
              A directory for parents and guardians searching for the right
              nursery, primary and secondary schools across the FCT.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-[#DAA520] mb-3">
              Explore
            </div>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/browse" className="hover:text-[#DAA520]">
                  All Schools
                </Link>
              </li>
              <li>
                <Link to="/browse?type=Private" className="hover:text-[#DAA520]">
                  Private Schools
                </Link>
              </li>
              <li>
                <Link to="/browse?type=Public" className="hover:text-[#DAA520]">
                  Public Schools
                </Link>
              </li>
              <li>
                <Link to="/submit" className="hover:text-[#DAA520]">
                  List Your School
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-[#DAA520] mb-3">
              Information
            </div>
            <p className="text-sm text-stone-300 leading-relaxed">
              Schools are self-submitted and reviewed by our team. Information
              is provided for guidance only. Please verify details directly with
              the school.
            </p>
          </div>
        </div>
        <div className="border-t border-white/10 py-5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-xs text-stone-400 flex flex-col sm:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} Best Schools in Abuja</span>
            <span>Serving the Federal Capital Territory</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
